import { computed, nextTick, onMounted, ref, watch, type ComputedRef, type Ref } from "vue";
import useStore from "../store.ts";
import type { Component } from "../types.ts";
import { activateAllSubscriptions, buildElements, setSubscriptionsDeferred } from "../utils/ElementBuilder.ts";
import { resolveContainerTemplate, updateHidden } from "../utils/ElementUtils.ts";
import {updateAnchors, updateSubmitions} from "../utils/DataUtils.ts";
import { postRenderRequest, postUpdateGeometry } from "../worker/RenderWorkerManager.ts";
import {
    buildRuleFieldMap,
    collectChannelFields,
    collectRuleNames,
    mergeRuleFields,
    prefetchRules,
} from "../utils/ChannelFieldCollector.ts";

/* see docs/EXTRACTED_COMMENTS.md#c076 */
export function useLayoutLifecycle(opts: { onResize: () => void }): {
    elements: Ref<Component[]>;
    renderKey: ComputedRef<string>;
} {
    const store = useStore();
    const elements = ref<Component[]>([]);
    const renderKey = computed(() => `${store.layout}`);

    const buildElementsFromLayoutData = () => {
        elements.value = Object.keys(store.layoutData)
            .map(key => buildElements(store.layoutData, key, "", false, true))
            .filter(child => child !== null);
    };

    const refreshHiddenSweep = () => {
        updateAnchors();
        updateSubmitions();
        // Containers first: resolve each one's template from the store (default selection — rules
        // refine it later) so `flags.template_resolved` no longer waits on a Vue mount that never
        // comes for a subtree under a hidden ancestor. Deepest-first, so a parent's updateHidden
        // sees its children's staged flags.
        const containers = Object.values(store.components)
            .filter(item => item.type == "DataObjectContainer")
            .sort((a, b) => b.props.id.split(".").length - a.props.id.split(".").length);
        for (const item of containers) {
            resolveContainerTemplate(item);
        }
        for (const item of Object.values(store.components)) {
            if (item.type == "DataObjectContainer") continue;
            updateHidden(item.props.id);
        }
    };

    const refreshChannelFieldMap = async () => {
        const ruleNames = collectRuleNames();
        // see docs/EXTRACTED_COMMENTS.md#c785 (supersedes #c385)
        await prefetchRules(ruleNames);

        const fieldMap = collectChannelFields();
        const ruleFieldMap = buildRuleFieldMap();
        mergeRuleFields(fieldMap, ruleFieldMap);
        store.channelFieldMap.clear();
        fieldMap.forEach((set, channel) => {
            store.channelFieldMap.set(channel, new Set(set));
        });
    };

    const bulkGeometryRefresh = async () => {
        await nextTick();
        if (typeof requestAnimationFrame === "function") {
            await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
        }
        // see docs/EXTRACTED_COMMENTS.md#c386
        const ids = Object.keys(store.components);
        for (const id of ids) {
            postUpdateGeometry(id, "initial-mount");
        }

        // see docs/EXTRACTED_COMMENTS.md#c387
        await postRenderRequest(["initial-paint"]);
        for (const item of Object.values(store.components)) {
            if (item.flags && !item.flags.is_datasheet) {
                item.flags.geometry_settled = true;
            }
        }
    };

    const applyLayout = async () => {
        await store.loadData();
        // Re-arm the initial-load latch for THIS build. applyLayoutPayload arms it once per
        // payload, but runApplyLoop coalesces payloads: a second payload landing while a run
        // is in flight bumps applyEpoch and replays applyLayout without a further reset. That
        // replay wipes store.loaded and store.reset()s the channels, while initialFired stays
        // latched from the first run — and maybeFireInitial() early-returns on it, so nothing
        // ever sets store.loaded again and the boot stalls on "Finishing up". The latch belongs
        // to a tree build, not to a payload: one reset per build, unconditionally.
        store.socketManager.socketListener?.resetInitialLoadState?.();
        store.loaded = false;
        let start = new Date();
        console.log('Resetting store...');
        // see docs/EXTRACTED_COMMENTS.md#c786
        const liveRules: Record<string, string> = {};
        for (const [name, rule] of store.ruleManager.getRules()) {
            const raw = rule.getRawValue();
            if (raw) liveRules[name] = raw;
        }
        await store.reset();
        console.log(`Done resetting. Took: ${new Date(new Date().getTime() - start.getTime()).getTime()}ms`);
        // see docs/EXTRACTED_COMMENTS.md#c762
        store.socketManager.layoutManager?.seedRulesForBuild();
        if (Object.keys(liveRules).length > 0) {
            store.ruleManager.seedFromSnapshot(liveRules);
        }
        start = new Date();
        console.log('Loading elements...');
        elements.value = [];
        console.log(Object.keys(store.layoutData));
        // see docs/EXTRACTED_COMMENTS.md#c776
        setSubscriptionsDeferred(true);
        try {
            buildElementsFromLayoutData();
            // see docs/EXTRACTED_COMMENTS.md#c388
            store.layoutPending = false;
            console.log(elements.value);
            console.log(`Done loading elements. Took: ${new Date(new Date().getTime() - start.getTime()).getTime()}ms. Total elements: ${elements.value.length}`);

            refreshHiddenSweep();

            // Drop any recount armed against the outgoing sheet.
            store.socketManager.layoutManager?.cancelRowUsageRecount();

            // see docs/EXTRACTED_COMMENTS.md#c389
            if (store.cluster.enabled) {
                store.socketManager.layoutManager?.reportRowUsage(0, false);
            }

            await refreshChannelFieldMap();
        } finally {
            setSubscriptionsDeferred(false);
        }

        // see docs/EXTRACTED_COMMENTS.md#c390
        activateAllSubscriptions();

        // see docs/EXTRACTED_COMMENTS.md#c391
        if (store.channelManager.getChannels().length == 0) {
            store.loaded = true;
        }

        // see docs/EXTRACTED_COMMENTS.md#c392
        store.socketManager.socketListener?.markSubscriptionsActivated?.();

        // Resize layout to account for status bar
        opts.onResize();

        await bulkGeometryRefresh();
    };

    // see docs/EXTRACTED_COMMENTS.md#c394
    let applyEpoch = 0;
    let applyRunning = false;

    const runApplyLoop = async () => {
        if (applyRunning) return;    // the active run's loop tail picks it up
        applyRunning = true;
        try {
            let seen;
            do {
                seen = applyEpoch;
                await applyLayout();
            } while (seen !== applyEpoch);
        } finally {
            applyRunning = false;
        }
    };

    watch(() => store.layoutData, () => { applyEpoch++; void runApplyLoop(); });

    // see docs/EXTRACTED_COMMENTS.md#c395
    watch(() => store.layout, () => {
        elements.value = [];
    });

    // see docs/EXTRACTED_COMMENTS.md#c396
    watch(() => store.loaded, (isLoaded) => {
        if (isLoaded) store.layoutPending = false;
    });

    // see docs/EXTRACTED_COMMENTS.md#c397
    onMounted(() => {
        if (Object.keys(store.layoutData).length > 0) {
            applyEpoch++;
            void runApplyLoop();
        }
    });

    return { elements, renderKey };
}
